import React, { useMemo } from "react";

import { getCourseRequestsFromStudentRequests, isPrimaryCourseRequest } from "../../../js/Helpers";

const normalizeID = (value) => `${value}`;

const formatCourseHeader = (course) => {
  const code = course?.course_code || "";
  const name = course?.name || "";

  if(code && name) return `${code}: ${name}`;
  return code || name || "Untitled Course";
};

const formatTeacherName = (teacher) => {
  const last_name = teacher?.name || "";
  const first_name = teacher?.first_name || "";
  const display_name = `${last_name}${first_name ? `, ${first_name}` : ""}`.trim();

  return display_name || "Unassigned";
};

const formatNumber = (value, {blankZero = false, decimals = 1} = {}) => {
  const number_value = Number(value || 0);
  if(blankZero && number_value === 0) return "";
  if(Number.isInteger(number_value)) return `${number_value}`;

  return number_value.toFixed(decimals);
};

const formatAverageClassSize = (value) => {
  const number_value = Number(value || 0);
  if(number_value === 0) return "--";

  return formatNumber(number_value);
};

export default function SectioningReport({
  departmentInfo,
  departmentID,
  courses = [],
  teachers = [],
  students = [],
  sections = [],
  subsections = [],
  scheduleInfo,
}) {
  const reportData = useMemo(() => {
    const department_course_ids = new Set();
    const sectionById = new Map();
    const subsectionById = new Map();

    sections.forEach(section => sectionById.set(normalizeID(section.section_id), section));
    subsections.forEach(subsection => subsectionById.set(normalizeID(subsection.subsection_id), subsection));

    const department_courses = courses
      .filter(course => Array.isArray(course.departments) && course.departments.includes(departmentID))
      .sort((course_a, course_b) => {
        const code_a = course_a.course_code || "";
        const code_b = course_b.course_code || "";
        if(code_a !== code_b) return code_a.localeCompare(code_b, undefined, {numeric: true});

        return (course_a.name || "").localeCompare(course_b.name || "");
      });

    department_courses.forEach(course => department_course_ids.add(normalizeID(course.course_id)));

    const teacher_ids_with_sections = new Set();
    const counts = {};
    const teacher_totals = {};
    const counted_section_keys = new Set();

    sections.forEach(section => {
      if(`${section.section_type}` !== "1") return;
      if(`${section.is_lab}` === "1") return;
      if(!department_course_ids.has(normalizeID(section.course_id))) return;

      if(scheduleInfo?.separate_ICR_codes === "0")
      {
        const subsection_info = subsectionById.get(normalizeID(section.section_id));
        if(subsection_info && `${section.is_inclusion}` === "1")
        {
          const main_section_info = sectionById.get(normalizeID(subsection_info.section_id));
          if(main_section_info && normalizeID(main_section_info.course_id) === normalizeID(section.course_id)) return;
        }
      }

      const teacher_ids = [
        section.teacher_id,
        ...(Array.isArray(section.secondary_teachers) ? section.secondary_teachers : []),
      ].filter(Boolean);

      teacher_ids.forEach(teacher_id => {
        const teacher_key = normalizeID(teacher_id);
        const course_key = normalizeID(section.course_id);
        const section_key = section.span_id ? `${teacher_key}:${course_key}:span:${section.span_id}` : `${teacher_key}:${course_key}:section:${section.section_id}`;

        if(counted_section_keys.has(section_key)) return;
        counted_section_keys.add(section_key);

        teacher_ids_with_sections.add(teacher_key);
        if(!(teacher_key in counts)) counts[teacher_key] = {};
        if(!(course_key in counts[teacher_key])) counts[teacher_key][course_key] = 0;
        counts[teacher_key][course_key] += 1;
        teacher_totals[teacher_key] = (teacher_totals[teacher_key] || 0) + 1;
      });
    });

    const department_teachers = teachers
      .filter(teacher => (Array.isArray(teacher.departments) && teacher.departments.includes(departmentID)) || teacher_ids_with_sections.has(normalizeID(teacher.teacher_id)))
      .sort((teacher_a, teacher_b) => {
        const name_a = formatTeacherName(teacher_a);
        const name_b = formatTeacherName(teacher_b);
        return name_a.localeCompare(name_b);
      });

    const request_counts = {};
    students.forEach(student => {
      const student_requests = getCourseRequestsFromStudentRequests(student.student_requests, {includeDeleted: false});
      student_requests.forEach(request => {
        const course_key = normalizeID(request.course_id);
        if(!department_course_ids.has(course_key)) return;

        if(!(course_key in request_counts)) request_counts[course_key] = {total: 0, primary: 0, alternates: 0};

        request_counts[course_key].total += 1;
        if(isPrimaryCourseRequest(request)) request_counts[course_key].primary += 1;
        else request_counts[course_key].alternates += 1;
      });
    });

    const sections_created_by_course = Object.values(counts).reduce((results, teacher_counts) => {
      Object.keys(teacher_counts).forEach(course_key => {
        results[course_key] = (results[course_key] || 0) + teacher_counts[course_key];
      });

      return results;
    }, {});

    const summary = department_courses.reduce((results, course) => {
      const course_key = normalizeID(course.course_id);
      const course_request_counts = request_counts[course_key] || {total: 0, primary: 0, alternates: 0};
      const requests = course_request_counts.total;
      const primary_requests = course_request_counts.primary;
      const alternate_requests = course_request_counts.alternates;
      const default_seat_count = parseInt(course.max_class_size, 10) || 0;
      const sections_needed = default_seat_count > 0 ? Math.ceil(primary_requests / default_seat_count) : 0;
      const sections_created = sections_created_by_course[course_key] || 0;
      const average_class_size = (primary_requests === 0 || sections_created === 0) ? 0 : primary_requests / sections_created;

      results[course_key] = {
        requests,
        primary_requests,
        alternate_requests,
        default_seat_count,
        sections_needed,
        sections_created,
        average_class_size,
      };

      return results;
    }, {});

    return {
      courses: department_courses,
      teachers: department_teachers,
      counts,
      teacher_totals,
      summary,
    };
  }, [courses, teachers, students, sections, subsections, scheduleInfo, departmentID]);

  const department_name = departmentInfo?.department || "Department";

  return (
    <div className="sectioning-report">
      <div className="sectioning-report-header">
        <div>
          <h1>Sectioning Report</h1>
          <div className="sectioning-report-subtitle">{department_name} Department</div>
        </div>
      </div>

      <div className="sectioning-report-table-shell">
        <table className="sectioning-report-table">
          <thead>
            <tr>
              <th className="sectioning-report-teacher-header">Teacher</th>
              {reportData.courses.map(course => (
                <th className="sectioning-report-course-header" key={course.course_id}>
                  {formatCourseHeader(course)}
                </th>
              ))}
              <th className="sectioning-report-total-header">Total</th>
            </tr>
          </thead>
          <tbody>
            {reportData.teachers.map(teacher => {
              const teacher_key = normalizeID(teacher.teacher_id);

              return (
                <tr key={teacher.teacher_id}>
                  <th className="sectioning-report-teacher-cell">{formatTeacherName(teacher)}</th>
                  {reportData.courses.map(course => {
                    const course_key = normalizeID(course.course_id);
                    const count = reportData.counts[teacher_key]?.[course_key] || 0;

                    return (
                      <td className="sectioning-report-number-cell" key={`${teacher.teacher_id}-${course.course_id}`}>
                        {formatNumber(count, {blankZero: true})}
                      </td>
                    );
                  })}
                  <td className="sectioning-report-total-cell">{formatNumber(reportData.teacher_totals[teacher_key] || 0, {blankZero: true})}</td>
                </tr>
              );
            })}
            <tr className="sectioning-report-summary-spacer">
              <th></th>
              {reportData.courses.map(course => <td key={`spacer-${course.course_id}`}></td>)}
              <td></td>
            </tr>
            <tr className="sectioning-report-summary-row">
              <th>Number of Requests</th>
              {reportData.courses.map(course => {
                const course_summary = reportData.summary[normalizeID(course.course_id)] || {};

                return (
                  <td className="sectioning-report-number-cell" key={`requests-${course.course_id}`}>
                    <div>{formatNumber(course_summary.primary_requests)} primary</div>
                    {course_summary.alternate_requests > 0 && (
                      <div className="sectioning-report-request-alternates">{formatNumber(course_summary.alternate_requests)} alternates</div>
                    )}
                  </td>
                );
              })}
              <td></td>
            </tr>
            <tr className="sectioning-report-summary-row">
              <th>Default Seat Count</th>
              {reportData.courses.map(course => (
                <td className="sectioning-report-number-cell" key={`seat-count-${course.course_id}`}>{formatNumber(reportData.summary[normalizeID(course.course_id)]?.default_seat_count)}</td>
              ))}
              <td></td>
            </tr>
            <tr className="sectioning-report-summary-row">
              <th>Number of Sections Needed</th>
              {reportData.courses.map(course => (
                <td className="sectioning-report-number-cell" key={`needed-${course.course_id}`}>{formatNumber(reportData.summary[normalizeID(course.course_id)]?.sections_needed)}</td>
              ))}
              <td></td>
            </tr>
            <tr className="sectioning-report-summary-row">
              <th>Number of Sections Created</th>
              {reportData.courses.map(course => {
                const course_summary = reportData.summary[normalizeID(course.course_id)] || {};
                const has_mismatch = course_summary.sections_needed !== course_summary.sections_created;
                const mismatch_title = has_mismatch ? `Sections needed (${course_summary.sections_needed || 0}) does not match sections created (${course_summary.sections_created || 0}).` : undefined;

                return (
                  <td
                    className={`sectioning-report-number-cell ${has_mismatch ? "sectioning-report-mismatch-cell" : ""}`}
                    key={`created-${course.course_id}`}
                    title={mismatch_title}
                  >
                    {formatNumber(course_summary.sections_created)}
                  </td>
                );
              })}
              <td></td>
            </tr>
            <tr className="sectioning-report-summary-row">
              <th>Average Class Size</th>
              {reportData.courses.map(course => (
                <td className="sectioning-report-number-cell" key={`average-${course.course_id}`}>{formatAverageClassSize(reportData.summary[normalizeID(course.course_id)]?.average_class_size)}</td>
              ))}
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
